import React, { useState } from 'react';

export const ArticlesLanding = ({
  onConditionClick,
  onArticleClick,
  conditions = [
    { name: 'Lupus', color: '#9b59b6', articleCount: 15, active: true },
    { name: 'Narcolepsy', color: '#385cbc', articleCount: 8, active: true },
    { name: 'Polycythemia Vera', color: '#f15b41', articleCount: 10, active: true },
    { name: 'Cancer', color: '#999', articleCount: 0, active: false },
    { name: 'Diabetes', color: '#999', articleCount: 0, active: false },
    { name: 'Asthma', color: '#999', articleCount: 0, active: false },
    { name: 'IBD', color: '#999', articleCount: 0, active: false }
  ],
  featuredArticles = [
    {
      id: 1,
      condition: 'Lupus',
      title: 'Can You Die From Lupus? What 650 Real Patients Want You to Know',
      excerpt: 'The question haunts nearly every lupus patient. We asked 650 real patients about their fears and experiences.',
      readTime: '8 min',
      date: 'Jan 20, 2025',
      featured: true
    },
    {
      id: 2,
      condition: 'Diabetes',
      title: 'Managing Type 2 Diabetes: Patient Stories of Success',
      excerpt: 'Real patients share their journeys to better blood sugar control and improved quality of life.',
      readTime: '6 min',
      date: 'Jan 18, 2025'
    },
    {
      id: 3,
      condition: 'Cancer',
      title: 'Navigating Cancer Treatment: What Patients Wish They Knew',
      excerpt: 'Insights from hundreds of cancer patients on making treatment decisions.',
      readTime: '10 min',
      date: 'Jan 15, 2025'
    }
  ]
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredCondition, setHoveredCondition] = useState(null);

  const filteredConditions = conditions.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredArticles = featuredArticles.filter(a =>
    a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.condition.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#272726',
        overflowX: 'hidden'
      }}
    >
      {/* Animated blob keyframes */}
      <style>{`
        @keyframes floatBlob1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.05); }
          66% { transform: translate(-20px, 20px) scale(0.95); }
        }
        @keyframes floatBlob2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, 30px) scale(0.95); }
          66% { transform: translate(20px, -30px) scale(1.05); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>

      {/* Hero Section */}
      <header
        style={{
          position: 'relative',
          padding: '100px 20px 80px',
          background: `
            radial-gradient(ellipse 80% 50% at 20% 40%, rgba(129, 156, 209, 0.25) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 80% 20%, rgba(250, 177, 132, 0.2) 0%, transparent 50%),
            radial-gradient(ellipse 70% 50% at 70% 80%, rgba(56, 92, 188, 0.15) 0%, transparent 50%),
            linear-gradient(180deg, #d0c7c7 0%, #e8e4e0 100%)
          `,
          overflow: 'hidden',
          textAlign: 'center'
        }}
      >
        {/* Floating blobs */}
        <div
          style={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '200px',
            height: '200px',
            borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
            background: 'rgba(129, 156, 209, 0.15)',
            filter: 'blur(40px)',
            animation: 'floatBlob1 20s ease-in-out infinite',
            pointerEvents: 'none'
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: '180px',
            height: '180px',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            background: 'rgba(250, 177, 132, 0.2)',
            filter: 'blur(40px)',
            animation: 'floatBlob2 25s ease-in-out infinite',
            pointerEvents: 'none'
          }}
        />

        <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(40px, 7vw, 64px)',
              fontWeight: '700',
              marginTop: 0,
              marginBottom: '20px',
              lineHeight: 1.1
            }}
          >
            Patient <span style={{ color: '#f15b41' }}>Stories</span> & Insights
          </h1>
          <p
            style={{
              fontSize: 'clamp(18px, 2.5vw, 22px)',
              color: '#444',
              maxWidth: '600px',
              margin: '0 auto 40px',
              lineHeight: 1.6
            }}
          >
            Real experiences from real patients. Find answers, discover community, and learn what others wish they knew.
          </p>

          {/* Search Bar */}
          <div
            style={{
              maxWidth: '500px',
              margin: '0 auto',
              position: 'relative'
            }}
          >
            <input
              type="text"
              placeholder="Search conditions or articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '18px 24px 18px 56px',
                fontSize: '17px',
                border: 'none',
                borderRadius: '50px',
                backgroundColor: 'white',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <svg
              style={{
                position: 'absolute',
                left: '22px',
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0.5
              }}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>
        </div>

        {/* Wavy bottom */}
        <svg
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '60px',
            pointerEvents: 'none'
          }}
          viewBox="0 0 1440 60"
          preserveAspectRatio="none"
        >
          <path
            d="M0,30 C200,60 400,0 600,30 C800,60 1000,10 1200,30 C1350,45 1400,25 1440,35 L1440,60 L0,60 Z"
            fill="#ffffff"
          />
        </svg>
      </header>

      {/* Conditions Grid */}
      <section
        style={{
          padding: '60px 20px',
          backgroundColor: '#ffffff'
        }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: '32px',
              fontWeight: '700',
              marginTop: 0,
              marginBottom: '12px',
              textAlign: 'center'
            }}
          >
            Browse by Condition
          </h2>
          <p
            style={{
              fontSize: '17px',
              color: '#666',
              textAlign: 'center',
              marginBottom: '40px'
            }}
          >
            Select a condition to explore articles from patients like you
          </p>

          {/* Clean Text Grid */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '16px',
              maxWidth: '900px',
              margin: '0 auto'
            }}
          >
            {filteredConditions.map((condition, index) => (
              <button
                key={condition.name}
                onClick={() => condition.active && onConditionClick && onConditionClick(condition.name)}
                onMouseEnter={() => condition.active && setHoveredCondition(condition.name)}
                onMouseLeave={() => setHoveredCondition(null)}
                style={{
                  padding: '16px 28px',
                  backgroundColor: condition.active && hoveredCondition === condition.name ? '#f7f5f3' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: condition.active ? 'pointer' : 'default',
                  transition: 'all 0.25s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  opacity: condition.active ? 1 : 0.5
                }}
              >
                {/* Colored dot accent */}
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: condition.color,
                    flexShrink: 0
                  }}
                />
                {/* Condition name */}
                <span
                  style={{
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: condition.active ? '#272726' : '#999',
                    transition: 'color 0.25s ease'
                  }}
                >
                  {condition.name}
                </span>
                {/* Article count or Coming Soon - shows on hover for active */}
                <span
                  style={{
                    fontSize: '14px',
                    color: '#888',
                    opacity: condition.active ? (hoveredCondition === condition.name ? 1 : 0) : 1,
                    transition: 'opacity 0.25s ease',
                    marginLeft: '4px',
                    fontStyle: condition.active ? 'normal' : 'italic'
                  }}
                >
                  {condition.active ? `(${condition.articleCount})` : 'Coming soon'}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section
        style={{
          padding: '60px 20px 80px',
          backgroundColor: '#f7f5f3',
          position: 'relative'
        }}
      >
        {/* Wavy top */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '50px',
            pointerEvents: 'none'
          }}
          viewBox="0 0 1440 50"
          preserveAspectRatio="none"
        >
          <path
            d="M0,25 C300,50 600,0 900,25 C1100,45 1300,10 1440,30 L1440,0 L0,0 Z"
            fill="#ffffff"
          />
        </svg>

        <div style={{ maxWidth: '1100px', margin: '0 auto', paddingTop: '20px' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: '32px',
              fontWeight: '700',
              marginTop: 0,
              marginBottom: '40px',
              textAlign: 'center'
            }}
          >
            {searchQuery ? 'Search Results' : 'Featured Articles'}
          </h2>

          {filteredArticles.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', fontSize: '18px' }}>
              No articles found matching "{searchQuery}"
            </p>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '28px'
              }}
            >
              {filteredArticles.map((article, index) => {
                const conditionData = conditions.find(c => c.name === article.condition) || { color: '#f15b41' };
                return (
                  <article
                    key={article.id}
                    onClick={() => onArticleClick && onArticleClick(article)}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)';
                    }}
                  >
                    {/* Color banner */}
                    <div
                      style={{
                        height: '8px',
                        background: `linear-gradient(90deg, ${conditionData.color} 0%, ${conditionData.color}99 100%)`
                      }}
                    />
                    <div style={{ padding: '24px 28px' }}>
                      {/* Condition tag */}
                      <span
                        style={{
                          display: 'inline-block',
                          backgroundColor: `${conditionData.color}15`,
                          color: conditionData.color,
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          marginBottom: '16px'
                        }}
                      >
                        {article.condition}
                      </span>

                      <h3
                        style={{
                          fontFamily: '"Source Serif Pro", Georgia, serif',
                          fontSize: '20px',
                          fontWeight: '600',
                          lineHeight: 1.35,
                          marginTop: 0,
                          marginBottom: '12px',
                          color: '#272726'
                        }}
                      >
                        {article.title}
                      </h3>

                      <p
                        style={{
                          fontSize: '15px',
                          color: '#666',
                          lineHeight: 1.6,
                          marginBottom: '16px'
                        }}
                      >
                        {article.excerpt}
                      </p>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '13px',
                          color: '#888'
                        }}
                      >
                        <span>{article.date}</span>
                        <span>{article.readTime} read</span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
